import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  useGetProjectByIdQuery,
  useGetZendeskEmailStatusFieldsQuery,
  useUpdateIntegrationMutation,
} from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import { useAppSelector } from 'src/store';
import { ProductsTypeBack } from 'src/constants/products';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { useForm } from 'react-hook-form';
import { TicketStatusSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/TicketStatusSelect/TicketStatusSelect';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const InitialStatusFields: { id: string | number | null; agent_label: string }[] = [
  { id: null, agent_label: 'don’t change ticket status' },
];

// The statuses are set once the status options load.
interface TicketStatusFormValues {
  ticket_status_after_ai_response: string;
  ticket_status_after_escalation: string;
}

// The agent's default status is stored as "no change" (null).
const toStoredStatus = (status: string | null | undefined) =>
  !status || status === InitialStatusFields[0].agent_label ? null : status;

export const ZendeskEmailStep3 = ({
  onClick = () => {},
}: {
  onClick?: (integration?: SelectedIntegration) => void;
}) => {
  const notification = useNotification();
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const [statusFields, setStatusFields] = useState(InitialStatusFields);
  const {
    data: statusFieldsResponse,
    isLoading: isLoadingStatusFields,
    isFetching: isFetchingStatusFields,
    isError: isErrorStatusFields,
  } = useGetZendeskEmailStatusFieldsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      type: ProductsTypeBack[currentProject?.project_type],
    },
    { skip: !selectedIntegration?.id || !currentProject },
  );

  const [
    updateIntegration,
    { data: updateIntegrationData, isLoading: isLoadingUpdateIntegration },
  ] = useUpdateIntegrationMutation();

  const { handleSubmit, control, setValue } = useForm({
    defaultValues: async () => {},
  });

  useEffect(() => {
    if (statusFieldsResponse) {
      const fields = [...InitialStatusFields, ...statusFieldsResponse].map((field) => {
        return {
          ...field,
          agent_label: field.agent_label.toLowerCase(),
        };
      });

      setStatusFields(fields);
    }
  }, [statusFieldsResponse]);

  useLoaderWhile(isLoadingStatusFields, isLoadingUpdateIntegration);

  useEffect(() => {
    if (updateIntegrationData) {
      notification.success('Integration Updated.');
      onClick({ ...selectedIntegration, ...updateIntegrationData });
    }
  }, [updateIntegrationData]);

  useEffect(() => {
    if (statusFields) {
      updateStatuses();
    }
  }, [selectedIntegration, statusFields]);

  const updateStatuses = () => {
    if (selectedIntegration.integration_data.ticket_status_after_ai_response) {
      const selectedField = statusFields.find(
        (field) =>
          field.agent_label ===
          selectedIntegration.integration_data.ticket_status_after_ai_response,
      );

      selectedField
        ? setValue('ticket_status_after_ai_response', selectedField.agent_label)
        : setValue('ticket_status_after_ai_response', InitialStatusFields[0].agent_label);
    } else {
      setValue('ticket_status_after_ai_response', InitialStatusFields[0].agent_label);
    }

    if (selectedIntegration.integration_data.ticket_status_after_escalation) {
      const selectedField = statusFields.find(
        (field) =>
          field.agent_label === selectedIntegration.integration_data.ticket_status_after_escalation,
      );

      selectedField
        ? setValue('ticket_status_after_escalation', selectedField.agent_label)
        : setValue('ticket_status_after_escalation', InitialStatusFields[0].agent_label);
    } else {
      setValue('ticket_status_after_escalation', InitialStatusFields[0].agent_label);
    }
  };

  const submit = async (formData: TicketStatusFormValues) => {
    const request = {
      status: selectedIntegration.status,
      integration_type: selectedIntegration.integration_type,
      integration_data: {
        ...selectedIntegration.integration_data,
        ticket_status_after_ai_response: toStoredStatus(formData.ticket_status_after_ai_response),
        ticket_status_after_escalation: toStoredStatus(formData.ticket_status_after_escalation),
      },
    };

    updateIntegration({
      projectId: id,
      integrationId: selectedIntegration.id,
      body: request,
    });
  };

  return (
    <form id="ticket-status-form" onSubmit={handleSubmit(submit)}>
      <div className="content">
        <LoaderWrapper
          loader={<Skeleton className="w-full h-16" />}
          isLoading={isFetchingStatusFields}
        >
          {!isErrorStatusFields ? (
            statusFields.length > 1 && (
              <div className={'flex flex-col gap-5'}>
                <TicketStatusSelect
                  label={
                    <div className={'flex flex-row gap-2 items-center mb-1'}>
                      Ticket status after AI response{' '}
                      <InputInfoToolTip
                        infoToolTip={{
                          side: 'right',
                          description: (
                            <>Status assigned to a ticket after the AI sends a response.</>
                          ),
                        }}
                      />
                    </div>
                  }
                  control={control}
                  name={'ticket_status_after_ai_response'}
                  options={statusFields}
                />
                <TicketStatusSelect
                  label={
                    <div className={'flex flex-row gap-2 items-center mb-1'}>
                      Ticket status after AI escalation{' '}
                      <InputInfoToolTip
                        infoToolTip={{
                          side: 'right',
                          description: (
                            <>Status assigned to a ticket after the AI escalates it to a human.</>
                          ),
                        }}
                      />
                    </div>
                  }
                  control={control}
                  name={'ticket_status_after_escalation'}
                  options={statusFields}
                />
              </div>
            )
          ) : (
            <p>
              The Zendesk details you entered seem invalid. Please double-check your subdomain,
              email, and API token following{' '}
              <a href={selectedIntegration.setupGuideUrl} target="_blank" rel="noreferrer">
                this guide
              </a>
              {'.'}
            </p>
          )}
        </LoaderWrapper>
      </div>
    </form>
  );
};
